import React from "react";
import type { Options } from "@ui/Highcharts";
import t from "../../../../../../i18n";
import type { Quantity } from "../../../../../../api/useQuantities";
import type { Point } from "highcharts";
import formatNumber from "@ui/i18n/formatNumber";

export interface FailureEvolutionHookOptions {
  series: Highcharts.SeriesBarOptions[];
  performanceIndicator: Quantity;
}

const useOptions = ({ series, performanceIndicator }: FailureEvolutionHookOptions) =>
  React.useMemo(() => {
    const _: Options = {
      initial: {},
      chart: {
        type: "bar",
        marginTop: 19,
        marginRight: 5,
        marginBottom: 5,
        marginLeft: 5,
      },
      deferSeriesRender: false,
      plotOptions: {
        bar: {
          borderRadius: 4,
          stacking: "normal",
          groupPadding: 0.22,
          pointPadding: -0.32,
          borderWidth: 3,

          states: {
            select: {
              enabled: false,
            },
            hover: {
              enabled: false,
            },
            inactive: {
              opacity: 1,
            },
          },
          dataLabels: {
            enabled: true,
            verticalAlign: "middle",
            formatter() {
              const point = this.point as Point;
              const bbox = point.graphic?.getBBox();
              if (bbox && bbox.height < 70) {
                return "";
              }
              if (this.y === undefined || (this.y !== undefined && this.y > -0.1 && this.y < 0.1)) {
                return "";
              }
              const unit = performanceIndicator.unit;
              return formatNumber(this.y, { precision: 1, unit });
            },
          },
        },
        series: {
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      yAxis: {
        opposite: false,
        gridLineWidth: 0,
        labels: {
          enabled: false,
        },
        stackLabels: {
          enabled: false,
        },
        maxPadding: 0,
        minPadding: 0,
        plotLines: [
          {
            value: 0,
            color: "#545862",
            width: 1,
            zIndex: 5,
            label: {
              text: "0",
              align: "center",
              verticalAlign: "top",
              x: -0.5,
              y: -10,
              rotation: 0,
            },
          },
        ],
      },
      xAxis: {
        visible: false,
        categories: [
          t("project.fault_events.charts.losses_repartition.categories.system"),
          t("project.fault_events.charts.losses_repartition.categories.total"),
        ],
      },
      legend: {
        enabled: false,
      },
      series,
    };
    return _;
  }, [series, performanceIndicator]);

export default useOptions;
